'use client';

import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { BarChart3, Coins, Info, Users } from 'lucide-react';

import { useT } from '@/hooks';

interface ReferralStatsProps {
  totalReferred: number;
  totalVolume: number;
  estimatedRewards: number;
}

/**
 * ReferralStats Component
 * Displays a grid of key performance indicators for the referral program using MUI.
 */
export const ReferralStats: React.FC<ReferralStatsProps> = ({
  totalReferred,
  totalVolume,
  estimatedRewards,
}) => {
  const { t } = useT();
  const stats = [
    {
      label: t('referral.totalReferred'),
      value: totalReferred,
      icon: Users,
      color: 'info.main',
      bgColor: 'info.lighter',
      tooltip: t('referral.totalReferredTooltip'),
    },
    {
      label: t('referral.volumeGenerated'),
      value: `$${totalVolume.toLocaleString()}`,
      icon: BarChart3,
      color: 'secondary.main',
      bgColor: 'secondary.lighter',
      tooltip: t('referral.volumeGeneratedTooltip'),
    },
    {
      label: t('referral.estRewards'),
      value: `$${estimatedRewards.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      icon: Coins,
      color: 'success.main',
      bgColor: 'success.lighter',
      tooltip: t('referral.estRewardsTooltip'),
    },
  ];

  const renderStatCard = (stat: (typeof stats)[0], index: number) => {
    return (
      <Paper
        key={stat.label}
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 4,
          p: 2.5,
          flex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1,
              borderRadius: 2,
              bgcolor: stat.bgColor,
              color: stat.color,
            }}
          >
            <stat.icon size={20} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              {stat.label}
            </Typography>
            <Tooltip title={stat.tooltip} arrow placement="top">
              <Box
                sx={{
                  display: 'flex',
                  cursor: 'help',
                  color: 'text.disabled',
                  '&:hover': { color: 'text.secondary' },
                }}
              >
                <Info size={14} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {stat.value}
        </Typography>
      </Paper>
    );
  };

  const renderedStats = stats.map(renderStatCard);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2,
        mt: 3,
      }}
    >
      {renderedStats}
    </Box>
  );
};
